import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { configToCssVars } from './theme';
import {
  RAIL_NODES,
  TrackingPageAd,
  TrackingPageAdPlacement,
  TrackingPageData,
  TrackingShipment,
} from './types';

/**
 * The shopper-facing tracking page.
 *
 * Deliberately props-only — it performs no data fetching — so the merchant
 * preview in app.shipaid.com can render the exact same tree against fixture
 * data instead of maintaining a second, drifting copy of the design.
 */

const TONE_COLORS: Record<string, string> = {
  neutral: '#64748b',
  progress: 'var(--tp-primary)',
  success: '#15803d',
  warning: '#b45309',
  danger: '#b91c1c',
};

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function Surface({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={classNames('p-6 sm:p-7 shadow-sm', className)}
      style={{
        background: 'var(--tp-surface)',
        borderRadius: 'var(--tp-radius)',
      }}
    >
      {children}
    </section>
  );
}

function BrandHeader({ data }: { data: TrackingPageData }) {
  const { branding } = data.branding;
  const name = data.storeName ?? 'Order tracking';

  return (
    <header className="flex items-center justify-between gap-4 py-5">
      {branding.logoUrl ? (
        <img
          src={branding.logoUrl}
          alt={name}
          style={{ height: `${branding.logoHeight}px` }}
          className="w-auto object-contain"
        />
      ) : (
        <span className="text-lg font-bold tracking-tight">{name}</span>
      )}
      {data.orderNumber ? (
        <span className="text-sm opacity-70">
          Order <span className="font-semibold">{data.orderNumber}</span>
        </span>
      ) : null}
    </header>
  );
}

function StatusHero({
  shipment,
  data,
}: {
  shipment: TrackingShipment;
  data: TrackingPageData;
}) {
  const eta = formatDate(shipment.estimatedDeliveryDate);
  const headline = data.branding.copy.headline;
  const isException = shipment.statusVariant === 'exception';

  return (
    <Surface>
      <p className="text-xs uppercase tracking-widest opacity-60 mb-2">
        {data.recipientName ? `Hi ${data.recipientName}` : 'Your shipment'}
      </p>
      <h1
        className="text-3xl sm:text-4xl font-bold leading-tight"
        style={{ color: TONE_COLORS[shipment.statusTone] ?? 'var(--tp-text)' }}
      >
        {shipment.statusLabel}
      </h1>
      {headline ? <p className="mt-2 opacity-80">{headline}</p> : null}

      {shipment.statusVariant === 'unknown' ? (
        <p className="mt-3 text-sm opacity-70">
          The carrier has not scanned this shipment yet. Tracking updates will
          appear here as soon as they do.
        </p>
      ) : null}

      {eta && !isException && shipment.status !== 'CANCELLED' ? (
        <dl className="mt-4">
          <dt className="text-xs uppercase tracking-widest opacity-60">
            Estimated delivery
          </dt>
          <dd className="text-lg font-semibold">{eta}</dd>
        </dl>
      ) : null}

      <p className="mt-4 text-sm opacity-70">
        {shipment.trackingCompany} &middot;{' '}
        {shipment.carrierTrackingUrl ? (
          <a
            className="underline"
            href={shipment.carrierTrackingUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shipment.trackingNumber}
          </a>
        ) : (
          shipment.trackingNumber
        )}
      </p>
    </Surface>
  );
}

function ExceptionBanner({ shipment }: { shipment: TrackingShipment }) {
  if (
    shipment.statusVariant !== 'exception' &&
    shipment.statusVariant !== 'cancelled'
  ) {
    return null;
  }

  const danger = shipment.statusTone === 'danger';

  return (
    <div
      role="alert"
      className="p-4 text-sm"
      style={{
        borderRadius: 'var(--tp-radius)',
        background: danger ? '#fef2f2' : '#fffbeb',
        color: danger ? '#991b1b' : '#92400e',
      }}
    >
      <strong className="font-semibold">{shipment.statusLabel}.</strong>{' '}
      {shipment.status === 'RETURN_TO_SENDER'
        ? 'This shipment is on its way back to the sender. Contact the store if you still need it.'
        : shipment.status === 'CANCELLED'
        ? 'This shipment was cancelled. Contact the store if you were expecting it.'
        : 'The carrier reported a problem delivering this shipment. Contact the store if it does not resolve.'}
    </div>
  );
}

function ProgressRail({ shipment }: { shipment: TrackingShipment }) {
  // A cancelled or unmoved shipment has no meaningful progress to draw.
  if (
    shipment.statusVariant === 'cancelled' ||
    shipment.statusVariant === 'unknown'
  ) {
    return null;
  }

  const nodes =
    shipment.statusVariant === 'pickup'
      ? [
          ...RAIL_NODES.slice(0, 2),
          { status: shipment.status, label: 'Ready for pickup' },
        ]
      : RAIL_NODES;

  return (
    <Surface>
      <ol className="flex items-start" aria-label="Delivery progress">
        {nodes.map((node, index) => {
          const reached = index <= shipment.nodeIndex;
          const current = index === shipment.nodeIndex;

          return (
            <li
              key={node.status}
              data-reached={reached ? 'true' : 'false'}
              data-current={current ? 'true' : 'false'}
              className="flex-1 flex flex-col items-center text-center relative"
            >
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute top-2 right-1/2 left-0 h-1"
                  style={{
                    background: reached ? 'var(--tp-primary)' : '#e2e8f0',
                  }}
                />
              ) : null}
              {index < nodes.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute top-2 left-1/2 right-0 h-1"
                  style={{
                    background:
                      index < shipment.nodeIndex
                        ? 'var(--tp-primary)'
                        : '#e2e8f0',
                  }}
                />
              ) : null}
              <span
                aria-hidden="true"
                className={classNames(
                  'relative z-10 rounded-full',
                  current ? 'w-5 h-5' : 'w-4 h-4',
                )}
                style={{
                  background: reached ? 'var(--tp-primary)' : '#e2e8f0',
                  outline: current ? '3px solid var(--tp-surface)' : 'none',
                  marginTop: current ? '-2px' : 0,
                }}
              />
              <span
                className={classNames(
                  'mt-2 text-xs sm:text-sm px-1',
                  current ? 'font-semibold' : 'opacity-60',
                )}
              >
                {node.label}
              </span>
            </li>
          );
        })}
      </ol>
    </Surface>
  );
}

function CheckpointTimeline({ shipment }: { shipment: TrackingShipment }) {
  if (!shipment.checkpoints.length) {
    return (
      <Surface>
        <h2 className="text-base font-semibold mb-2">Tracking history</h2>
        <p className="text-sm opacity-70">No carrier scans yet.</p>
      </Surface>
    );
  }

  return (
    <Surface>
      <h2 className="text-base font-semibold mb-4">Tracking history</h2>
      <div className="space-y-5">
        {shipment.checkpoints.map((group) => (
          <div key={group.date}>
            <h3 className="text-xs uppercase tracking-widest opacity-60 mb-2">
              {group.date}
            </h3>
            <ul className="space-y-3">
              {group.checkpoints.map((checkpoint, index) => (
                <li
                  key={`${group.date}-${index}`}
                  className="flex gap-3 text-sm"
                >
                  <span className="w-16 shrink-0 opacity-60">
                    {checkpoint.time}
                  </span>
                  <span
                    aria-hidden="true"
                    className="mt-1.5 w-2 h-2 rounded-full shrink-0"
                    style={{ background: 'var(--tp-primary)' }}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {checkpoint.description || checkpoint.message}
                    </span>
                    {checkpoint.address ? (
                      <span className="block opacity-60">
                        {checkpoint.address}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Surface>
  );
}

function ItemList({ shipment }: { shipment: TrackingShipment }) {
  if (!shipment.items.length) return null;

  return (
    <Surface>
      <h2 className="text-base font-semibold mb-4">In this shipment</h2>
      <ul className="space-y-3">
        {shipment.items.map((item, index) => (
          <li
            key={`${item.title}-${index}`}
            className="flex items-center gap-3"
          >
            <span
              className="w-14 h-14 shrink-0 overflow-hidden bg-black bg-opacity-5"
              style={{ borderRadius: 'calc(var(--tp-radius) / 2)' }}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : null}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium truncate">{item.title}</span>
              <span className="block text-sm opacity-60">
                Qty {item.quantity}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Surface>
  );
}

function AdSlot({ ads }: { ads: TrackingPageAd[] }) {
  if (!ads.length) return null;

  return (
    <>
      {ads.map((ad) => (
        <a
          key={ad.id}
          href={ad.ctaUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="block overflow-hidden shadow-sm hover:opacity-95 transition-opacity"
          style={{
            background: 'var(--tp-surface)',
            borderRadius: 'var(--tp-radius)',
          }}
        >
          <img
            data-testid="promotion-image"
            src={ad.imageUrl}
            alt=""
            className="w-full h-auto max-h-72 object-contain"
          />
          <span className="block p-4">
            {ad.headline ? (
              <span className="block font-semibold">{ad.headline}</span>
            ) : null}
            {ad.body ? (
              <span className="block text-sm opacity-70 mt-1">{ad.body}</span>
            ) : null}
            {ad.ctaLabel ? (
              <span
                className="inline-block mt-3 px-4 py-2 text-sm font-semibold text-white"
                style={{
                  background: 'var(--tp-primary)',
                  borderRadius: 'var(--tp-radius)',
                }}
              >
                {ad.ctaLabel}
              </span>
            ) : null}
          </span>
        </a>
      ))}
    </>
  );
}

function SupportFooter({ data }: { data: TrackingPageData }) {
  const { copy, branding } = data.branding;

  return (
    <footer className="py-8 text-center text-sm opacity-70 space-y-2">
      {copy.supportEmail || copy.supportUrl ? (
        <p>
          Need help?{' '}
          {/* Both are shown when both are set — a merchant who fills in each
              means to offer both routes, not to have one hide the other. */}
          {copy.supportUrl ? (
            <a
              className="underline"
              href={copy.supportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact {data.storeName ?? 'the store'}
            </a>
          ) : null}
          {copy.supportUrl && copy.supportEmail ? <span> or </span> : null}
          {copy.supportEmail ? (
            <a className="underline" href={`mailto:${copy.supportEmail}`}>
              {copy.supportEmail}
            </a>
          ) : null}
        </p>
      ) : null}
      {branding.showPoweredBy ? (
        <p className="opacity-60">Tracking powered by ShipAid</p>
      ) : null}
    </footer>
  );
}

/**
 * Mirrors the API's `normaliseAds`, which drops slots missing either value.
 *
 * The API already filters these out, but the merchant preview renders the raw
 * editor config over postMessage — so without this an in-progress ad would show
 * a card whose empty href resolves to the preview page itself.
 */
function isRenderableAd(ad: TrackingPageAd): boolean {
  return Boolean(ad.imageUrl?.trim()) && Boolean(ad.ctaUrl?.trim());
}

function adsFor(
  data: TrackingPageData,
  placement: TrackingPageAdPlacement,
): TrackingPageAd[] {
  if (!data.branding.sections.ads) return [];
  return data.branding.ads.filter(
    (ad) => ad.placement === placement && isRenderableAd(ad),
  );
}

export function TrackingPageView({ data }: { data: TrackingPageData }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Guard against the active tab pointing past the end when shipments change,
  // which happens as the merchant preview swaps fixtures.
  useEffect(() => {
    if (activeIndex > data.shipments.length - 1) setActiveIndex(0);
  }, [data.shipments.length, activeIndex]);

  const shipment = data.shipments[activeIndex] ?? data.shipments[0];
  const { sections } = data.branding;

  return (
    <div
      data-testid="tracking-page-root"
      style={{
        ...configToCssVars(data.branding),
        background: 'var(--tp-bg)',
        color: 'var(--tp-text)',
        fontFamily: 'var(--tp-font)',
        minHeight: '100vh',
      }}
    >
      <div
        data-testid="tracking-shell"
        className="mx-auto w-full max-w-6xl px-4 sm:px-6 pb-12"
      >
        <BrandHeader data={data} />

        {data.shipments.length > 1 ? (
          <div
            role="tablist"
            aria-label="Shipments"
            className="flex gap-2 mb-4 overflow-x-auto"
          >
            {data.shipments.map((s, index) => (
              <button
                key={s.trackingNumber}
                role="tab"
                type="button"
                aria-selected={index === activeIndex}
                onClick={() => setActiveIndex(index)}
                className="px-4 py-2 text-sm font-medium whitespace-nowrap"
                style={{
                  borderRadius: 'var(--tp-radius)',
                  background:
                    index === activeIndex
                      ? 'var(--tp-primary)'
                      : 'var(--tp-surface)',
                  color: index === activeIndex ? '#ffffff' : 'var(--tp-text)',
                }}
              >
                Shipment {index + 1}
              </button>
            ))}
          </div>
        ) : null}

        {shipment ? (
          <div
            data-testid="tracking-layout"
            className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start"
          >
            {/* Status, cover and items on one side; the progress rail and scan
                history on the other once there is room for two columns. */}
            <div className="space-y-4">
              <ExceptionBanner shipment={shipment} />
              {sections.hero ? (
                <StatusHero shipment={shipment} data={data} />
              ) : null}
              {sections.items ? <ItemList shipment={shipment} /> : null}
              <AdSlot ads={adsFor(data, 'below_items')} />
            </div>

            <div
              data-testid="tracking-column-progress"
              className="space-y-4 mt-4 lg:mt-0"
            >
              {sections.progress ? <ProgressRail shipment={shipment} /> : null}
              <AdSlot ads={adsFor(data, 'above_timeline')} />
              {sections.timeline ? (
                <CheckpointTimeline shipment={shipment} />
              ) : null}
              <AdSlot ads={adsFor(data, 'below_timeline')} />
            </div>
          </div>
        ) : null}

        {sections.support ? <SupportFooter data={data} /> : null}
      </div>
    </div>
  );
}
